import React, { useState, useEffect } from "react";
import axios from "axios";

export default function AdminAssignExam() {
  const [backendUrl, setBackendUrl] = useState("");
  const [tab, setTab] = useState("create");

  const [courses, setCourses] = useState([]);
  const [exams, setExams] = useState([]);
  const [students, setStudents] = useState([]);
  const [groups, setGroups] = useState([]);

  // ---------- Create Exam ----------
  const [examName, setExamName] = useState("");
  const [examType, setExamType] = useState("");
  const [totalMarks, setTotalMarks] = useState("");
  const [timeLimit, setTimeLimit] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [passingPercentage, setPassingPercentage] = useState("");
  const [requireCompletion, setRequireCompletion] = useState(false);

  // ---------- Group Assignment ----------
  const [selectedExam, setSelectedExam] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groupStartDate, setGroupStartDate] = useState("");
  const [groupEndDate, setGroupEndDate] = useState("");
  const [groupStatus, setGroupStatus] = useState("Active");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);

  const [message, setMessage] = useState("");

 // ---------- Load backend URL ----------
useEffect(() => {
  const script = document.createElement("script");
  script.src = "/config.js";

  script.onload = () => {
    if (window.env?.BACKEND_URL) {
      console.log(
        "ASSIGN EXAM BACKEND URL >>>",
        window.env.BACKEND_URL
      );
      setBackendUrl(window.env.BACKEND_URL);
    } else {
      console.error("BACKEND_URL not found in config.js");
    }
  };

  script.onerror = () => {
    console.error("Failed to load /config.js");
  };

  document.body.appendChild(script);

  return () => {
    document.body.removeChild(script);
  };
}, []);


// ---------- Load Exam Management Data ----------
useEffect(() => {
  if (!backendUrl) {
    console.log("Waiting for backend URL...");
    return;
  }

  console.log("Loading Exam Management data from:", backendUrl);

  (async () => {
    try {
      const [courseRes, examRes, studentRes, groupRes] =
        await Promise.all([
          axios.get(`${backendUrl}/courses`),
          axios.get(`${backendUrl}/exams/list`),
          axios.get(`${backendUrl}/users?role=Student`),
          axios.get(`${backendUrl}/exam-groups/list`)
        ]);

      console.log("COURSES FROM BACKEND >>>", courseRes.data);
      console.log("EXAMS FROM BACKEND >>>", examRes.data);
      console.log("STUDENTS FROM BACKEND >>>", studentRes.data);
      console.log("EXAM GROUPS FROM BACKEND >>>", groupRes.data);

      setCourses(
        courseRes.data.map(c => ({
          id: c.ID ?? c.course_id,
          name: c.Course_Name ?? c.course_name
        }))
      );

      setExams(
        examRes.data.map(e => ({
          id: e.Exam_ID,
          name: e.Exam_Name
        }))
      );

      setStudents(studentRes.data);
      setGroups(groupRes.data);

    } catch (err) {
      console.error(
        "❌ Error loading Exam Management data:",
        err
      );
    }
  })();

}, [backendUrl, tab]);


  // ---------- Create Exam ----------
  const handleCreateExam = async () => {
    if (
      !examName ||
      !examType ||
      !totalMarks ||
      !timeLimit ||
      !selectedCourse ||
      !startDate ||
      !endDate ||
      !passingPercentage
    ) {
      setMessage("⚠️ All fields are required.");
      return;
    }

    try {
      await axios.post(`${backendUrl}/exams/create`, {
        examName,
        examType,
        totalMarks: Number(totalMarks),
        timeLimit: Number(timeLimit),
        courseId: Number(selectedCourse),
        startDate,
        endDate,
        passingPercentage: Number(passingPercentage),
        requiresCompletion: requireCompletion ? 1 : 0
      });

      setMessage("✅ Exam created successfully!");
      setExamName("");
      setExamType("");
      setTotalMarks("");
      setTimeLimit("");
      setSelectedCourse("");
      setStartDate("");
      setEndDate("");
      setPassingPercentage("");
      setRequireCompletion(false);

    } catch (err) {
      console.error(err);
      setMessage("❌ Failed to create exam.");
    }
  };

  // ---------- Create Group ----------
  const handleCreateGroup = async () => {
    if (!selectedExam || !groupName || !groupStartDate || !groupEndDate) {
      setMessage("⚠️ Please fill all group fields.");
      return;
    }

    try {
      await axios.post(`${backendUrl}/exam-groups/create`, {
        examId: Number(selectedExam),
        groupName,
        startDate: groupStartDate,
        endDate: groupEndDate,
        status: groupStatus
      });

      setMessage("✅ Group created successfully!");
      setGroupName("");
      setGroupStartDate("");
      setGroupEndDate("");

      const refreshed = await axios.get(`${backendUrl}/exam-groups/list`);
      setGroups(refreshed.data);

    } catch (err) {
      console.error(err);
      setMessage("❌ Failed to create exam group.");
    }
  };

  // ---------- Assign Students ----------
  const handleAssignStudents = async () => {
    if (!selectedGroup || selectedStudents.length === 0) {
      setMessage("⚠️ Select group and students.");
      return;
    }

    try {
      await axios.post(`${backendUrl}/exam-groups/assign-students`, {
        groupId: Number(selectedGroup),
        studentIds: selectedStudents.map(Number)
      });

      setMessage("✅ Students assigned successfully!");
      setSelectedStudents([]);
      setSelectedGroup("");

    } catch (err) {
      console.error(err);
      setMessage("❌ Assignment failed.");
    }
  };

  // ---------- Toggle Select All ----------
  const handleSelectAll = () => {
    setSelectedStudents(
      selectedStudents.length === students.length
        ? []
        : students.map(s => String(s.User_ID))
    );
  };

  return (
    <div style={container}>
      <h2 style={header}>Exam Management</h2>

      <div style={tabBar}>
        <button onClick={() => setTab("create")} style={tab === "create" ? activeTab : inactiveTab}>
          Create Exam
        </button>
        <button onClick={() => setTab("group")} style={tab === "group" ? activeTab : inactiveTab}>
          Assign Exam to Group
        </button>
      </div>

      {/* ---------- Create Exam ---------- */}
      {tab === "create" && (
        <div style={card}>
          <h3 style={subHeader}>Create New Exam</h3>

          <label>Course:</label>
          <select value={selectedCourse} onChange={e => setSelectedCourse(e.target.value)} style={input}>
            <option value="">-- Select Course --</option>
            {courses.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <input placeholder="Exam Name" value={examName} onChange={e => setExamName(e.target.value)} style={input} />
          <input placeholder="Exam Type" value={examType} onChange={e => setExamType(e.target.value)} style={input} />
          <input type="number" placeholder="Total Marks" value={totalMarks} onChange={e => setTotalMarks(e.target.value)} style={input} />
          <input type="number" placeholder="Time Limit (minutes)" value={timeLimit} onChange={e => setTimeLimit(e.target.value)} style={input} />

          <label>Start Date:</label>
          <input type="datetime-local" value={startDate} onChange={e => setStartDate(e.target.value)} style={input} />

          <label>End Date:</label>
          <input type="datetime-local" value={endDate} onChange={e => setEndDate(e.target.value)} style={input} />

          <input type="number" placeholder="Passing Percentage" value={passingPercentage} onChange={e => setPassingPercentage(e.target.value)} style={input} />

          <label>
            <input type="checkbox" checked={requireCompletion} onChange={e => setRequireCompletion(e.target.checked)} />
            Require Course Material Completion
          </label>

          <button onClick={handleCreateExam} style={button}>Create Exam</button>
        </div>
      )}

      {/* ---------- Assign Exam to Group ---------- */}
      {tab === "group" && (
        <div style={card}>
          <h3 style={subHeader}>Assign Exam to Group</h3>

          <label>Select Exam:</label>
          <select value={selectedExam} onChange={e => setSelectedExam(e.target.value)} style={input}>
            <option value="">-- Select Exam --</option>
            {exams.map(e => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>

          <input placeholder="Group Name" value={groupName} onChange={e => setGroupName(e.target.value)} style={input} />
          <label>Group Start Date:</label>
          <input type="datetime-local" value={groupStartDate} onChange={e => setGroupStartDate(e.target.value)} style={input} />
          <label>Group End Date:</label>
          <input type="datetime-local" value={groupEndDate} onChange={e => setGroupEndDate(e.target.value)} style={input} />

          <button onClick={handleCreateGroup} style={button}>Create Group</button>

          <hr />

          <label>Select Group:</label>
          <select value={selectedGroup} onChange={e => setSelectedGroup(e.target.value)} style={input}>
            <option value="">-- Select Group --</option>
            {groups.map(g => (
              <option key={g.Group_ID} value={g.Group_ID}>{g.Group_Name}</option>
            ))}
          </select>

          <button onClick={handleSelectAll} style={{ marginBottom: 10 }}>
            {selectedStudents.length === students.length ? "Deselect All" : "Select All"}
          </button>

          {students.map(s => (
            <label key={s.User_ID}>
              <input
                type="checkbox"
                value={s.User_ID}
                checked={selectedStudents.includes(String(s.User_ID))}
                onChange={e => {
                  const id = e.target.value;
                  setSelectedStudents(prev =>
                    e.target.checked ? [...prev, id] : prev.filter(x => x !== id)
                  );
                }}
              />
              {s.Username}
            </label>
          ))}

          <button onClick={handleAssignStudents} style={button}>Assign Students</button>
        </div>
      )}

      {message && <p style={{ textAlign: "center", color: message.startsWith("✅") ? "green" : "red" }}>{message}</p>}
    </div>
  );
}

/* ---------- Styles ---------- */
const container = { maxWidth: 950, margin: "20px auto", padding: 25 };
const header = { textAlign: "center", color: "#E30016" };
const tabBar = { display: "flex", justifyContent: "center", gap: 10 };
const activeTab = { background: "#E30016", color: "#fff", padding: 10 };
const inactiveTab = { background: "#eee", padding: 10 };
const card = { padding: 20, marginTop: 20 };
const input = { width: "100%", padding: 10, marginBottom: 10 };
const button = { background: "#E30016", color: "#fff", padding: 12 };
const subHeader = { color: "#E30016" };
